/* eslint-disable svelte/prefer-svelte-reactivity */
import {
	CognitoIdentityProviderClient,
	ConfirmForgotPasswordCommand,
	ConfirmSignUpCommand,
	ForgotPasswordCommand,
	InitiateAuthCommand,
	ResendConfirmationCodeCommand,
	RespondToAuthChallengeCommand,
	type ConfirmSignUpCommandOutput,
	type ForgotPasswordCommandOutput,
} from "@aws-sdk/client-cognito-identity-provider";
import {
	fromCognitoIdentityPool,
	type CognitoIdentityCredentialProvider,
	type CognitoIdentityCredentials,
} from "@aws-sdk/credential-provider-cognito-identity";
import * as Sentry from "@sentry/svelte";
import { z } from "zod/v4";
import type { LoginResponse } from "@libs/aws-cognito/LoginResponse.js";
import type { OAuthError } from "@libs/aws-cognito/OAuthError.js";
import type { OAuthResponse } from "@libs/aws-cognito/OAuthResponse.js";
import type { SessionResponse } from "@libs/aws-cognito/SessionResponse.js";
import { parseJwt } from "@libs/schema/parseJwt.js";
import { diagnostics } from "../../diagnostics.js";
import { retryFailedToFetch } from "../../retryFailedToFetch.js";
import { localStorageState } from "../../svelte/localStorageState.svelte.js";

const sessionSchema = z
	.object({
		AccessToken: z.string().optional(),
		ExpiresIn: z.number().optional(),
		IdToken: z.string().optional(),
		RefreshToken: z.string().optional(),
		TokenType: z.string().optional(),
	})
	.catch({});

const credentialsSchema = z
	.object({
		accessKeyId: z.string(),
		secretAccessKey: z.string(),
		sessionToken: z.string().optional(),
		expiration: z.coerce.date().optional(),
		identityId: z.string().optional(),
		authenticated: z.boolean().optional(),
	})
	.nullable();

export class CognitoClient {
	private readonly cognitoIdentityProviderClient: CognitoIdentityProviderClient;
	private readonly session = localStorageState(sessionSchema, "cognito-session", {}, diagnostics);
	private readonly credentials = localStorageState(credentialsSchema, "cognito-credentials", null, diagnostics);
	public readonly accessToken = $derived(this.session.value.AccessToken);

	constructor(
		private readonly region: string,
		private readonly clientId: string,
		private readonly identityPoolId: string,
		private readonly userPoolId: string,
		private readonly allowUnauthenticated: boolean,
		private readonly domain: string | null,
		private readonly endpoint?: string | null,
		private readonly createClients?: (credentialProvider: CognitoIdentityCredentialProvider) => void | Promise<void>,
	) {
		this.cognitoIdentityProviderClient = new CognitoIdentityProviderClient({
			region,
			...(endpoint ? { endpoint } : {}),
		});
		void this.createClients?.(this.createCredentialsProvider());
	}

	public createCredentialsProvider(): CognitoIdentityCredentialProvider {
		const provider = fromCognitoIdentityPool({
			clientConfig: { region: this.region },
			identityPoolId: this.identityPoolId,
			logins:
				this.session.value.RefreshToken !== undefined || !this.allowUnauthenticated
					? {
							[`cognito-idp.${this.region}.amazonaws.com/${this.userPoolId}`]: this.waitForIdToken.bind(this),
						}
					: undefined,
		});

		return async () => {
			try {
				const creds = this.credentials.value;
				if (creds && (!creds.expiration || creds.expiration > new Date())) {
					return $state.snapshot(creds) as CognitoIdentityCredentials;
				}

				const credentials = await retryFailedToFetch(provider);
				this.credentials.value = credentials;
				return credentials;
			} catch (error) {
				Sentry.captureException(error);
				await this.logout();
				throw error;
			}
		};
	}

	public waitForIdToken(): Promise<string> {
		return new Promise((resolve) => {
			const interval = setInterval(() => {
				void (async (): Promise<void> => {
					const session = await this.getSession();
					if (session?.idToken) {
						clearInterval(interval);
						resolve(session.idToken);
					}
				})();
			}, 1000);
		});
	}

	public async getSession(): Promise<SessionResponse | null> {
		try {
			if (!this.session.value.RefreshToken) {
				return null;
			}

			if (this.session.value.IdToken) {
				const parsed = parseJwt(this.session.value.IdToken);
				if ((parsed.exp ?? 0) * 1000 > Date.now()) {
					return {
						idToken: this.session.value.IdToken,
						payload: parsed,
					};
				}
			}

			const command = new InitiateAuthCommand({
				AuthFlow: "REFRESH_TOKEN",
				AuthParameters: { REFRESH_TOKEN: this.session.value.RefreshToken },
				ClientId: this.clientId,
			});

			const { AuthenticationResult } = await retryFailedToFetch(() => this.cognitoIdentityProviderClient.send(command));

			if (AuthenticationResult) {
				this.session.value = {
					...this.session.value,
					AccessToken: AuthenticationResult.AccessToken,
					ExpiresIn: AuthenticationResult.ExpiresIn,
					IdToken: AuthenticationResult.IdToken,
					RefreshToken: AuthenticationResult.RefreshToken ?? this.session.value.RefreshToken,
					TokenType: AuthenticationResult.TokenType,
				};

				const idToken = AuthenticationResult.IdToken;
				if (!idToken) {
					return null;
				}

				return {
					idToken,
					payload: parseJwt(idToken),
				};
			}

			return null;
		} catch (error) {
			Sentry.captureException(error);
			await this.logout();
			throw error;
		}
	}

	public getProviderLoginUrl(state: string, redirectUri: string): string {
		if (!this.domain) {
			throw new Error("Cognito domain is not configured");
		}

		const url = new URL(`https://${this.domain}/oauth2/authorize`);
		url.searchParams.set("client_id", this.clientId);
		url.searchParams.set("response_type", "code");
		url.searchParams.set("state", state);
		url.searchParams.set("identity_provider", "Google");
		url.searchParams.set("redirect_uri", redirectUri);
		url.searchParams.set("prompt", "select_account");

		return url.toString();
	}

	public async loginWithProvider(code: string, redirectUri: string): Promise<LoginResponse> {
		if (!this.domain) {
			throw new Error("Cognito domain is not configured");
		}

		const url = new URL(`https://${this.domain}/oauth2/token`);
		const data = new URLSearchParams();
		data.set("client_id", this.clientId);
		data.set("grant_type", "authorization_code");
		data.set("redirect_uri", redirectUri);
		data.set("code", code);
		const headers = { "content-type": "application/x-www-form-urlencoded" };
		const response = await fetch(url, { method: "POST", headers, body: data.toString() });

		if (!response.ok) {
			let error = "";
			try {
				error = await response.text();
				({ error } = JSON.parse(error) as OAuthError);
			} catch {
				// ignore
			}
			throw new Error(`OAuth2 token endpoint [${response.status}]: ${response.statusText}\n${error}`);
		}

		const auth = (await response.json()) as OAuthResponse;
		this.session.value = {
			AccessToken: auth.access_token,
			ExpiresIn: auth.expires_in,
			IdToken: auth.id_token,
			RefreshToken: auth.refresh_token,
			TokenType: auth.token_type,
		};
		return { SignedIn: true };
	}

	public async login(username: string, password: string): Promise<LoginResponse> {
		this.credentials.value = null;

		const command = new InitiateAuthCommand({
			AuthFlow: "USER_PASSWORD_AUTH",
			AuthParameters: { USERNAME: username, PASSWORD: password },
			ClientId: this.clientId,
		});

		try {
			const result = await retryFailedToFetch(() => this.cognitoIdentityProviderClient.send(command));
			if (result.AuthenticationResult) {
				this.session.value = {
					AccessToken: result.AuthenticationResult.AccessToken,
					ExpiresIn: result.AuthenticationResult.ExpiresIn,
					IdToken: result.AuthenticationResult.IdToken,
					RefreshToken: result.AuthenticationResult.RefreshToken,
					TokenType: result.AuthenticationResult.TokenType,
				};
				return { SignedIn: true };
			}
			return { SignedIn: false, Challenge: result.ChallengeName, Session: result.Session };
		} catch (error) {
			if (error instanceof Error && error.name === "PasswordResetRequiredException") {
				return { SignedIn: false, Challenge: "PASSWORD_RESET" };
			}
			if (error instanceof Error && error.name === "UserNotConfirmedException") {
				return { SignedIn: false, Challenge: "USER_NOT_CONFIRMED" };
			}
			throw error;
		}
	}

	public async logout(): Promise<void> {
		this.session.value = {};
		this.credentials.value = null;
		await this.createClients?.(this.createCredentialsProvider());
	}

	public async confirmNewPassword(session: string, username: string, newPassword: string): Promise<void> {
		const command = new RespondToAuthChallengeCommand({
			ChallengeName: "NEW_PASSWORD_REQUIRED",
			ClientId: this.clientId,
			ChallengeResponses: { USERNAME: username, NEW_PASSWORD: newPassword },
			Session: session,
		});
		// The challenge session is single-use: if a network error happens after Cognito already consumed it,
		// retrying with the same session throws NotAuthorizedException instead of succeeding, so don't retry here.
		const result = await this.cognitoIdentityProviderClient.send(command);
		if (!result.AuthenticationResult) {
			throw new Error("Failed to complete new password challenge");
		}
		this.session.value = {
			AccessToken: result.AuthenticationResult.AccessToken,
			ExpiresIn: result.AuthenticationResult.ExpiresIn,
			IdToken: result.AuthenticationResult.IdToken,
			RefreshToken: result.AuthenticationResult.RefreshToken,
			TokenType: result.AuthenticationResult.TokenType,
		};
	}

	public async confirmResetPassword(
		Username: string,
		Password: string,
		ConfirmationCode: string,
	): Promise<ConfirmSignUpCommandOutput> {
		const command = new ConfirmForgotPasswordCommand({
			ConfirmationCode,
			ClientId: this.clientId,
			Password,
			Username,
		});
		return await retryFailedToFetch(() => this.cognitoIdentityProviderClient.send(command));
	}

	public async forgotPassword(Username: string): Promise<ForgotPasswordCommandOutput> {
		const command = new ForgotPasswordCommand({
			Username,
			ClientId: this.clientId,
		});
		return await retryFailedToFetch(() => this.cognitoIdentityProviderClient.send(command));
	}

	public async confirmSignUp(Username: string, ConfirmationCode: string): Promise<ConfirmSignUpCommandOutput> {
		const command = new ConfirmSignUpCommand({
			ClientId: this.clientId,
			Username,
			ConfirmationCode,
		});
		return await retryFailedToFetch(() => this.cognitoIdentityProviderClient.send(command));
	}

	public async resendConfirmationCode(Username: string): Promise<void> {
		const command = new ResendConfirmationCodeCommand({
			ClientId: this.clientId,
			Username,
		});
		await retryFailedToFetch(() => this.cognitoIdentityProviderClient.send(command));
	}
}
